import { Link } from 'react-router-dom';
import { XCircle, MessageCircle, ArrowLeft } from 'lucide-react';

const WHATSAPP_NUMBER = '447934514016';

// Detect language from browser
const getBrowserLanguage = (): 'pt-BR' | 'en-GB' => {
  const lang = navigator.language || 'en-GB';
  return lang.startsWith('pt') ? 'pt-BR' : 'en-GB';
};

const t = {
  'pt-BR': {
    title: 'Pagamento cancelado',
    description: 'Você cancelou o processo de pagamento. Não se preocupe, nenhuma cobrança foi feita.',
    tryAgain: 'Se você mudou de ideia, pode tentar novamente a qualquer momento.',
    help: 'Precisa de ajuda?',
    helpDesc: 'Se tiver alguma dúvida sobre os planos ou pagamento, fale com a gente.',
    cta: 'Falar no WhatsApp',
    back: 'Voltar ao início',
  },
  'en-GB': {
    title: 'Payment cancelled',
    description: "You've cancelled the payment process. Don't worry, no charges were made.",
    tryAgain: 'If you changed your mind, you can try again anytime.',
    help: 'Need help?',
    helpDesc: 'If you have any questions about plans or payment, chat with us.',
    cta: 'Chat on WhatsApp',
    back: 'Back to home',
  },
};

export default function UpgradeCancelPage() {
  const lang = getBrowserLanguage();
  const texts = t[lang];

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    lang === 'pt-BR' ? 'Oi! Tive uma dúvida sobre os planos' : 'Hi! I had a question about the plans'
  )}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Nav */}
      <nav className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="KeepNota" className="h-8 w-auto" />
            <span className="font-bold text-xl text-gray-900 dark:text-white">KeepNota</span>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-lg mx-auto px-4 py-16 text-center">
        {/* Icon */}
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full mb-6">
          <XCircle className="w-10 h-10 text-gray-400" />
        </div>

        {/* Message */}
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
          {texts.title}
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mb-2">
          {texts.description}
        </p>
        <p className="text-gray-500 dark:text-gray-400 mb-8">
          {texts.tryAgain}
        </p>

        {/* Help Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg mb-8">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
            {texts.help}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
            {texts.helpDesc}
          </p>
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-500 text-white font-semibold px-6 py-3 rounded-full hover:bg-green-600 transition"
          >
            <MessageCircle className="w-5 h-5" />
            {texts.cta}
          </a>
        </div>

        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          {texts.back}
        </Link>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 text-center text-gray-500 dark:text-gray-400 text-sm">
        <p>© 2025 KeepNota by Fig & Rosa Ltd.</p>
      </footer>
    </div>
  );
}
